import { useEffect, useRef, useState } from 'react'
import { LayoutList, ListOrdered, Search } from 'lucide-react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Button } from '@/components/ui/button'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useActionTypeOptions, useActionsWithoutProject, useProjectsWithActions } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { ActionItemsTable, ActionItemsTableSkeleton } from '@/components/action-items-table'
import { withoutProjectPlace } from '@/features/action-items/without-project-place'
import { StaffFilter } from '@/components/staff-filter'
import { ProjectPriorityFilter } from '@/components/project-priority-filter'
import { ServiceTierFilter } from '@/components/service-tier'
import { PROJECT_PRIORITY_FILTER_OPTIONS, projectPriorityParam } from '@/lib/project-priority'
import { SERVICE_TIER_FILTER_OPTIONS, serviceTierParam } from '@/lib/service-tier'
import { cn, formatStatus, resultCount } from '@/lib/utils'
import { ActionQueue } from '@/features/action-items/action-queue'
import { useManufacturerDataActions } from '@/features/manufacturer-data/hooks'
import { DataActionGroups } from '@/features/manufacturer-data/data-action-groups'
import { SearchInput } from '@/components/search-input'

// In progress items stay listed but out of the default view (USA-1443): the list shows what still needs starting.
// Resolved lists closed items, the latest closed first, so staff can find one again to follow up (USA-1653).
const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'all', label: 'All open' },
  { value: 'resolved', label: 'Resolved' },
] as const

// The item's own priority (Abby's list, USA-1443), beside the project's (USA-1451).
const ITEM_PRIORITY_OPTIONS = [
  { value: 'all', label: 'Any item priority' },
  { value: 'high', label: 'High priority items' },
  { value: 'normal', label: 'Normal priority items' },
  { value: 'low', label: 'Low priority items' },
] as const

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'sales', label: 'Sales' },
  { value: 'operations', label: 'Operations' },
  { value: 'warehousing', label: 'Warehousing' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'finance', label: 'Finance' },
  { value: 'data', label: 'Data' },
]

export function ActionItemsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    role: parseAsString.withDefault('all'),
    priority: parseAsStringLiteral(PROJECT_PRIORITY_FILTER_OPTIONS).withDefault('all'),
    tier: parseAsStringLiteral(SERVICE_TIER_FILTER_OPTIONS).withDefault('all'),
    status: parseAsStringLiteral(['not_started', 'in_progress', 'all', 'resolved'] as const).withDefault('not_started'),
    item_priority: parseAsStringLiteral(['all', 'high', 'normal', 'low'] as const).withDefault('all'),
    type: parseAsString.withDefault('all'),
    staff: parseAsInteger,
    page: parseAsInteger.withDefault(1),
    view: parseAsStringLiteral(['table', 'queue'] as const).withDefault('table'),
  })
  const { role, priority, tier, status, item_priority: itemPriority, type, page } = filters
  // The queue walks open items; closed ones are only listed.
  const resolved = status === 'resolved'
  const view = resolved ? 'table' : filters.view
  const { data: typeOptions = [] } = useActionTypeOptions()
  const selectedStaffId = filters.staff ?? undefined
  const { showMyProjects, staffId } = useMyProjectsFilter()
  const actionOwnerId = showMyProjects ? staffId : selectedStaffId
  // A new owner starts at page 1; a page restored from the URL on Back is kept.
  const previousOwnerId = useRef(actionOwnerId)
  useEffect(() => {
    if (previousOwnerId.current === actionOwnerId) return
    previousOwnerId.current = actionOwnerId
    void setFilters({ page: 1 })
  }, [actionOwnerId, setFilters])

  const listParams = {
    search: search || undefined,
    staff_id: actionOwnerId,
    assigned_role: role === 'all' ? undefined : role,
    project_priority: projectPriorityParam(priority),
    service_tier: serviceTierParam(tier),
    priority: itemPriority === 'all' ? undefined : itemPriority,
    action_type: type === 'all' ? undefined : type,
  }
  // The Data role's items belong to manufacturers, not projects (USA-1474); they queue too (USA-1513).
  const isData = role === 'data'
  const dataParams = {
    search: search || undefined,
    // Mine: the Data owner on Staff Defaults sees them, nobody else (USA-1655).
    staff_id: actionOwnerId,
    priority: itemPriority === 'all' ? undefined : itemPriority,
    action_type: type === 'all' ? undefined : type,
  }
  const dataActions = useManufacturerDataActions(
    { ...dataParams, progress: status === 'all' ? undefined : status, page, limit: 20 },
    isData && view === 'table',
  )
  // Every agent's "Unable to take required action" items also belong to the Data role (USA-1697); they sit on
  // projects, so they list above the manufacturers' items. Ops owns them, so no staff filter applies here.
  const unableToAct = useProjectsWithActions(
    {
      ...listParams,
      staff_id: undefined,
      assigned_role: 'data',
      progress: status === 'all' ? undefined : status,
      page: 1,
      limit: 50,
    },
    view === 'table' && isData,
  )
  const projects = useProjectsWithActions(
    { ...listParams, progress: status === 'all' ? undefined : status, page, limit: 20 },
    view === 'table' && !isData,
  )
  // Invoice items whose invoice has no project lead the first page (USA-1184); a project filter hides them.
  const [withoutProjectExpanded, setWithoutProjectExpanded] = useState(false)
  const withoutProject = useActionsWithoutProject(
    {
      search: listParams.search,
      staff_id: listParams.staff_id,
      assigned_role: listParams.assigned_role,
      priority: listParams.priority,
      action_type: listParams.action_type,
      progress: status === 'all' ? undefined : status,
      limit: withoutProjectExpanded ? 500 : 5,
    },
    view === 'table' && !isData && priority === 'all' && tier === 'all',
  )
  const withoutProjectGroup =
    withoutProject.data && page === 1 && priority === 'all' && tier === 'all'
      ? {
          actions: withoutProject.data.actions ?? [],
          total: withoutProject.data.total ?? 0,
          expanded: withoutProjectExpanded,
          onToggle: () => setWithoutProjectExpanded((open) => !open),
        }
      : undefined
  const { data, isLoading } = isData ? dataActions : projects
  const showList = view === 'table'
  // Every matching item across all pages, the no-project group's included (USA-1634).
  const withoutProjectTotal = priority === 'all' && tier === 'all' ? withoutProject.data?.total : 0
  const itemTotal = isData
    ? dataActions.data && unableToAct.data
      ? dataActions.data.action_total + (unableToAct.data.action_total ?? 0)
      : undefined
    : projects.data && withoutProjectTotal != null
      ? projects.data.action_total + withoutProjectTotal
      : undefined

  // Filters set away from their defaults, counted on the Filters button.
  const activeFilters = [
    status !== 'not_started',
    itemPriority !== 'all',
    type !== 'all',
    role !== 'all',
    !isData && priority !== 'all',
    !isData && tier !== 'all',
    !isData && !showMyProjects && selectedStaffId != null,
  ].filter(Boolean).length

  return (
    <div className={cn('mx-auto space-y-4', showList ? 'max-w-7xl' : 'max-w-[1600px]')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Action Items</h1>
          <div
            role="group"
            aria-label="Action items view"
            className="inline-grid grid-cols-2 rounded-lg border bg-muted/40 p-1"
          >
            {(
              [
                { value: 'table', label: 'Table', Icon: LayoutList },
                { value: 'queue', label: 'Queue', Icon: ListOrdered },
              ] as const
            ).map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={view === value}
                disabled={resolved && value === 'queue'}
                title={resolved && value === 'queue' ? 'The queue walks open items' : undefined}
                onClick={() => void setFilters({ view: value, page: 1 })}
                className={cn(
                  'inline-flex min-h-8 cursor-pointer items-center justify-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
                  view === value && 'bg-background text-foreground shadow-sm',
                )}
              >
                <Icon className="size-3.5" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ListFilters
        count={showList ? resultCount(itemTotal, 'item') : undefined}
        active={activeFilters}
        onClear={() =>
          void setFilters({ role: null, priority: null, tier: null, status: null, item_priority: null, type: null, staff: null, page: 1 })
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search action items..."
              aria-label="Search action items"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Status">
        <Select
          value={status}
          onValueChange={(v) => {
            void setFilters({ status: v as typeof status, page: 1 })
          }}
        >
          <SelectTrigger aria-label="Action status filter" className="w-full">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </ListFilter>
        <ListFilter label="Item priority">
        <Select
          value={itemPriority}
          onValueChange={(v) => {
            void setFilters({ item_priority: v as typeof itemPriority, page: 1 })
          }}
        >
          <SelectTrigger aria-label="Action priority filter" className="w-full">
            <SelectValue placeholder="Item priority" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_PRIORITY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </ListFilter>
        <ListFilter label="Type">
        <SearchSelect
          ariaLabel="Action type filter"
          className="h-9 w-full"
          value={type === 'all' ? undefined : type}
          allLabel="All types"
          options={[
            ...typeOptions,
            // A type kept in the URL after its last item closed still shows as selected.
            ...(type !== 'all' && !typeOptions.some((o) => o.value === type)
              ? [{ value: type, label: formatStatus(type) }]
              : []),
          ]}
          onChange={(v) => {
            void setFilters({ type: v ?? 'all', page: 1 })
          }}
          searchPlaceholder="Search types"
        />
        </ListFilter>
        <ListFilter label="Role">
        <Select
          value={role}
          onValueChange={(v) => {
            void setFilters({ role: v, page: 1 })
          }}
        >
          <SelectTrigger aria-label="Action role filter" className="w-full">
            <SelectValue placeholder="Action role" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        </ListFilter>
        {!isData && (
          <ListFilter label="Project priority">
            <ProjectPriorityFilter
              value={priority}
              onValueChange={(value) => void setFilters({ priority: value, page: 1 })}
            />
          </ListFilter>
        )}
        {!isData && (
          <ListFilter label="Service tier">
            <ServiceTierFilter value={tier} onValueChange={(value) => void setFilters({ tier: value, page: 1 })} />
          </ListFilter>
        )}
        {!isData && (
          <ListFilter label="Staff">
            <StaffFilter
              value={showMyProjects ? staffId : selectedStaffId}
              disabled={showMyProjects}
              onValueChange={(value) => {
                void setFilters({ staff: value ?? null, page: 1 })
              }}
            />
          </ListFilter>
        )}
      </ListFilters>

      {view === 'queue' && status !== 'resolved' ? (
        <ActionQueue
          key={JSON.stringify({ ...listParams, status })}
          signature={JSON.stringify({ ...listParams, status })}
          params={listParams}
          dataParams={isData ? dataParams : undefined}
          status={status}
        />
      ) : isData ? (
        isLoading || unableToAct.isLoading ? (
          <ActionItemsTableSkeleton />
        ) : dataActions.data?.items?.length || unableToAct.data?.items?.length ? (
          <>
            {unableToAct.data?.items?.length ? (
              <section aria-labelledby="unable-to-act-heading" className="space-y-2">
                <h2 id="unable-to-act-heading" className="text-sm font-semibold">
                  Unable to take required action
                </h2>
                <ActionItemsTable items={unableToAct.data.items} />
              </section>
            ) : null}
            {dataActions.data?.items?.length ? <DataActionGroups items={dataActions.data.items} /> : null}
          </>
        ) : (
          <div className="py-12 text-center text-muted-foreground">{resolved ? 'No resolved action items' : 'No action items'}</div>
        )
      ) : isLoading ? (
        <ActionItemsTableSkeleton />
      ) : projects.data?.items?.length || withoutProjectGroup?.actions.length ? (
        <ActionItemsTable
          items={projects.data?.items ?? []}
          withoutProject={withoutProjectGroup}
          withoutProjectAt={withoutProjectPlace(projects.data?.items ?? [], withoutProjectGroup, resolved)}
        />
      ) : (
        <div className="py-12 text-center text-muted-foreground">{resolved ? 'No resolved action items' : 'No action items'}</div>
      )}

      {/* The Data view pages its manufacturers; one page of them needs no pager beside the agent items. */}
      {showList && !(isData && (data?.pages ?? 0) <= 1) && (
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
      )}
    </div>
  )
}
